// src/components/ClientPortalUsersModal.js

import React, { useState, useEffect, useRef } from 'react';
import { 
    collection, query, where, onSnapshot, doc, 
    setDoc, addDoc, updateDoc, deleteDoc, serverTimestamp, getDocs 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import { hashPassword } from '../utils/cryptoUtils';
import useConfirmationModal from './common/useConfirmationModal';
import PasswordInput from './common/PasswordInput';
import { EQUIPMENT_CONFIG } from '../config/equipmentConfig';

export default function ClientPortalUsersModal({ client, collectionName, onClose, permissionLevel, isPortalUser = false }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const fileInputRef = useRef(null);

    // Campos do formulário
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [role, setRole] = useState('User'); // 'User' ou 'User Company'
    const [active, setActive] = useState(true);

    // Permissões granulares para User Company
    const [modules, setModules] = useState({
        contracts: true,
        reports: true,
        assets: true,
        inventory: true,
        inventoryTypes: Object.keys(EQUIPMENT_CONFIG)
    });

    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', msg: '' });

    const { ConfirmationModal, showConfirmation } = useConfirmationModal();
    const portalUsersPath = `/artifacts/${appId}/public/data/portal_users`;

    // Escutar usuários da empresa selecionada (com match por ID e Nome para máxima resiliência)
    useEffect(() => {
        if (!client?.id) return;
        const q = query(collection(db, portalUsersPath));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            const clientIdStr = String(client.id);
            const clientNameNorm = (client.nomeFantasia || client.name || '').toLowerCase().trim();

            const list = allUsers.filter(u => {
                const uCompanyIdStr = String(u.companyId || '');
                const uCompanyNameNorm = (u.companyName || '').toLowerCase().trim();
                return (
                    (clientIdStr && uCompanyIdStr === clientIdStr) ||
                    (clientNameNorm && uCompanyNameNorm && uCompanyNameNorm === clientNameNorm)
                );
            });

            setUsers(list);
            setLoading(false);
        }, (error) => {
            console.error("Erro ao carregar usuários do portal:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [client?.id, client?.nomeFantasia, client?.name, portalUsersPath]);

    const handleOpenAdd = () => {
        setEditingUser(null);
        setName('');
        setEmail('');
        setPassword('');
        setPhone('');
        setRole('User');
        setModules({
            contracts: true,
            reports: true,
            assets: true,
            inventory: true,
            inventoryTypes: Object.keys(EQUIPMENT_CONFIG)
        });
        setActive(true);
        setFeedback({ type: '', msg: '' });
        setIsFormOpen(true);
    };

    const handleOpenEdit = (user) => {
        setEditingUser(user);
        setName(user.name || '');
        setEmail(user.email || '');
        setPassword(user.password || ''); // Carrega a senha gravada para visualização/edição
        setPhone(user.phone || '');
        setRole(user.role || 'User');
        setModules({
            contracts: user.modules?.contracts ?? true,
            reports: user.modules?.reports ?? true,
            assets: user.modules?.assets ?? true,
            inventory: user.modules?.inventory ?? true,
            inventoryTypes: user.modules?.inventoryTypes || Object.keys(EQUIPMENT_CONFIG)
        });
        setActive(user.active !== false);
        setFeedback({ type: '', msg: '' });
        setIsFormOpen(true);
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setFeedback({ type: '', msg: '' });

        if (!name.trim() || !email.trim()) {
            setFeedback({ type: 'error', msg: 'Nome e E-mail são obrigatórios.' });
            return;
        }

        const normalizedEmail = email.trim().toLowerCase();

        if (!editingUser && !password.trim()) {
            setFeedback({ type: 'error', msg: 'Defina uma senha para o novo usuário.' });
            return;
        }

        if (password && password.length < 6) {
            setFeedback({ type: 'error', msg: 'A senha deve conter no mínimo 6 caracteres.' });
            return;
        }

        setSaving(true);
        try {
            // Verificar unicidade de e-mail no portal_users
            const checkQuery = query(
                collection(db, portalUsersPath),
                where('email', '==', normalizedEmail)
            );
            const checkSnap = await getDocs(checkQuery);
            const emailInUse = checkSnap.docs.some(d => !editingUser || d.id !== editingUser.id);

            if (emailInUse) {
                setFeedback({ type: 'error', msg: 'Este e-mail já está cadastrado para outro usuário do portal.' });
                setSaving(false);
                return;
            }

            const finalRole = isPortalUser ? 'User' : role;
            const userData = {
                name: name.trim(),
                email: normalizedEmail,
                phone: phone.trim(),
                role: finalRole,
                modules: finalRole === 'User Company' ? modules : { contracts: false, reports: false, assets: false, inventory: false },
                active,
                companyId: String(editingUser?.companyId || client.id),
                companyName: (client.nomeFantasia || client.name || editingUser?.companyName || '').trim(),
                companyType: editingUser?.companyType || collectionName || 'clients',
                updatedAt: serverTimestamp()
            };

            if (password.trim()) {
                userData.password = password.trim();
                userData.passwordHash = await hashPassword(password.trim());
            }

            if (editingUser) {
                await updateDoc(doc(db, portalUsersPath, editingUser.id), userData);
                setFeedback({ type: 'success', msg: 'Usuário atualizado com sucesso!' });
            } else {
                userData.createdAt = serverTimestamp();
                await addDoc(collection(db, portalUsersPath), userData);
                setFeedback({ type: 'success', msg: 'Usuário criado com sucesso!' });
            }

            setTimeout(() => {
                setIsFormOpen(false);
                setEditingUser(null);
                setFeedback({ type: '', msg: '' });
            }, 800);
        } catch (error) {
            console.error("Erro ao salvar usuário do portal:", error);
            setFeedback({ type: 'error', msg: 'Erro ao salvar: ' + error.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = (user) => {
        showConfirmation({
            title: "Excluir Usuário do Portal",
            message: `Tem certeza de que deseja remover o acesso de "${user.name}" (${user.email})?`,
            confirmText: "Excluir",
            confirmColor: "bg-red-500",
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, portalUsersPath, user.id));
                } catch (err) {
                    console.error("Erro ao deletar usuário:", err);
                    alert("Erro ao excluir usuário: " + err.message);
                }
            }
        });
    };

    const canEdit = permissionLevel === 'edit';

    const filteredUsers = users.filter(u => 
        (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleExportPDF = () => {
        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Usuários - ${client.nomeFantasia || client.name}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                    </style>
                </head>
                <body>
                    <h2>Usuários do Portal - ${client.nomeFantasia || client.name}</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>E-mail</th>
                                <th>Telefone</th>
                                <th>Acesso</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredUsers.map(u => `
                                <tr>
                                    <td>${u.name}</td>
                                    <td>${u.email}</td>
                                    <td>${u.phone || '-'}</td>
                                    <td>${u.role}</td>
                                    <td>${u.active !== false ? 'Ativo' : 'Inativo'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <script>
                        window.onload = () => { window.print(); window.close(); }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleDownloadTemplate = () => {
        const csvContent = "data:text/csv;charset=utf-8,Nome Completo,Email,Senha,Telefone,Role\nExemplo Silva,exemplo@empresa.com.br,senha123,11999999999,User";
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "modelo_importacao.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const [csvPreviewData, setCsvPreviewData] = useState(null);
    const [isCsvPreviewModalOpen, setIsCsvPreviewModalOpen] = useState(false);

    const handleImportUsers = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            const text = event.target.result;
            const lines = text.split('\n').filter(line => line.trim() !== '');
            
            if (lines.length > 1) {
                const parsedData = [];
                for (let i = 1; i < lines.length; i++) {
                    const cols = lines[i].split(',');
                    const name = cols[0]?.trim();
                    const email = cols[1]?.trim().toLowerCase();
                    const pass = cols[2]?.trim();
                    const phone = cols[3]?.trim() || '';
                    const role = cols[4]?.trim() || 'User';
                    
                    const errors = [];
                    if (!name) errors.push("Nome ausente");
                    if (!email) errors.push("E-mail ausente");
                    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push("E-mail inválido");
                    if (!pass || pass.length < 6) errors.push("Senha ausente ou muito curta");
                    
                    if (email && users.find(u => u.email === email)) {
                        errors.push("E-mail já está na lista atual");
                    } else if (email && parsedData.find(p => p.email === email)) {
                        errors.push("E-mail duplicado neste mesmo arquivo");
                    }
                    
                    parsedData.push({
                        id: i, name, email, pass, phone, role, errors,
                        isValid: errors.length === 0
                    });
                }
                setCsvPreviewData(parsedData);
                setIsCsvPreviewModalOpen(true);
            } else {
                showGlobalFeedback('error', 'O arquivo CSV parece estar vazio ou sem linhas de dados válidas.');
            }
            if (fileInputRef.current) fileInputRef.current.value = '';
        };
        reader.readAsText(file);
    };

    const confirmImportCsv = async () => {
        const validUsers = csvPreviewData.filter(item => item.isValid);
        if (validUsers.length === 0) {
            showGlobalFeedback('error', 'Nenhum usuário válido para importar.');
            setIsCsvPreviewModalOpen(false);
            return;
        }

        setSaving(true);
        let importedCount = 0;
        
        for (const user of validUsers) {
            try {
                const checkQuery = query(collection(db, portalUsersPath), where('email', '==', user.email));
                const checkSnap = await getDocs(checkQuery);
                if (checkSnap.empty) {
                    const passHash = await hashPassword(user.pass);
                    await addDoc(collection(db, portalUsersPath), {
                        name: user.name, 
                        email: user.email, 
                        phone: user.phone, 
                        role: (user.role === 'User Company' ? 'User Company' : 'User'), 
                        active: true,
                        password: user.pass, 
                        passwordHash: passHash,
                        companyId: String(client.id),
                        companyName: (client.nomeFantasia || client.name || '').trim(),
                        companyType: collectionName || 'clients',
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        modules: user.role === 'User Company' ? { contracts: true, reports: true, assets: true, inventory: true, inventoryTypes: Object.keys(EQUIPMENT_CONFIG) } : { contracts: false, reports: false, assets: false, inventory: false }
                    });
                    importedCount++;
                }
            } catch (err) {
                console.error("Erro importando usuário do CSV:", err);
            }
        }
        
        setSaving(false);
        setIsCsvPreviewModalOpen(false);
        setCsvPreviewData(null);
        showGlobalFeedback('success', `${importedCount} usuários importados com sucesso.`);
    };

    const [sendingEmailId, setSendingEmailId] = useState(null);
    const [globalFeedback, setGlobalFeedback] = useState(null);

    const showGlobalFeedback = (type, msg) => {
        setGlobalFeedback({ type, msg });
        setTimeout(() => setGlobalFeedback(null), 5000);
    };

    const handleSendEmail = async (user) => {
        setSendingEmailId(user.id);
        try {
            const functions = getFunctions();
            const enviarCredenciais = httpsCallable(functions, 'enviarCredenciaisEmail');
            await enviarCredenciais({
                name: user.name,
                email: user.email,
                password: user.password || '',
                companyName: user.companyName || client.nomeFantasia || client.name
            });
            showGlobalFeedback('success', `Credenciais enviadas com sucesso para o e-mail de ${user.name}!`);
        } catch (error) {
            console.error("Erro ao disparar credenciais por e-mail:", error);
            showGlobalFeedback('error', "Não foi possível enviar o e-mail. Verifique as configurações ou tente novamente.");
        } finally {
            setSendingEmailId(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex justify-center items-center z-[130] p-4 animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl w-full max-w-6xl xl:max-w-7xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Cabeçalho */}
                <header className="p-6 sm:p-7 border-b border-slate-800 bg-slate-800/30 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl sm:text-2xl font-black text-white">Usuários do Portal Web</h3>
                            <p className="text-xs text-slate-400 mt-0.5">
                                Empresa: <strong className="text-cyan-400 font-semibold">{client.nomeFantasia || client.name}</strong>
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <input 
                            type="text" 
                            placeholder="Pesquisar..." 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500 w-48 sm:w-64"
                        />
                        <button 
                            onClick={onClose} 
                            className="text-slate-400 hover:text-white p-2.5 rounded-xl hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700"
                            title="Fechar"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </header>

                {/* Toast Global */}
                {globalFeedback && (
                    <div className={`absolute top-24 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
                        globalFeedback.type === 'error' ? 'bg-red-950/90 border-red-500/50 text-red-400' : 'bg-emerald-950/90 border-emerald-500/50 text-emerald-400'
                    }`}>
                        {globalFeedback.type === 'error' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                        <span className="font-semibold text-sm">{globalFeedback.msg}</span>
                    </div>
                )}

                {/* Conteúdo */}
                <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar relative">
                    {/* Barra de Ação Superior */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-800/40 p-4 rounded-2xl border border-slate-700/50">
                        <div>
                            <span className="text-sm font-bold text-white block">
                                {filteredUsers.length} {filteredUsers.length === 1 ? 'usuário listado' : 'usuários listados'}
                            </span>
                            <span className="text-xs text-slate-400">
                                Permite que funcionários deste cliente façam login na Intranet.
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {canEdit && (
                                <>
                                    <button onClick={handleExportPDF} className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-lg flex items-center gap-2 text-xs active:scale-95">
                                        Exportar PDF
                                    </button>
                                    <button onClick={handleDownloadTemplate} className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-lg flex items-center gap-2 text-xs active:scale-95" title="Baixar modelo de importação CSV">
                                        Modelo CSV
                                    </button>
                                    <label className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-lg flex items-center gap-2 text-xs cursor-pointer active:scale-95">
                                        Importar CSV
                                        <input type="file" accept=".csv" className="hidden" ref={fileInputRef} onChange={handleImportUsers} />
                                    </label>
                                    <button
                                        onClick={handleOpenAdd}
                                        className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2.5 px-5 rounded-xl transition-all shadow-lg shadow-cyan-900/30 flex items-center gap-2 text-xs uppercase tracking-wider active:scale-95 shrink-0"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Adicionar Usuário
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Formulário Embutido (quando ativo) */}
                    {isFormOpen && (
                        <form onSubmit={handleSaveUser} className="bg-slate-800/90 border border-cyan-500/40 rounded-2xl p-6 shadow-2xl space-y-4 animate-in fade-in slide-in-from-top-4 duration-200">
                            <div className="flex items-center justify-between border-b border-slate-700/80 pb-3">
                                <h4 className="text-sm font-black text-cyan-400 uppercase tracking-wider">
                                    {editingUser ? 'Editar Usuário do Portal' : 'Novo Usuário do Portal'}
                                </h4>
                                <button 
                                    type="button" 
                                    onClick={() => setIsFormOpen(false)}
                                    className="text-xs text-slate-400 hover:text-white"
                                >
                                    Cancelar
                                </button>
                            </div>

                            {feedback.msg && (
                                <div className={`p-3 rounded-xl text-xs font-semibold ${
                                    feedback.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                                }`}>
                                    {feedback.msg}
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Nome Completo *
                                    </label>
                                    <input 
                                        type="text" 
                                        required 
                                        value={name} 
                                        onChange={e => setName(e.target.value)} 
                                        placeholder="Ex: Carlos Silva"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        E-mail de Login *
                                    </label>
                                    <input 
                                        type="email" 
                                        required 
                                        value={email} 
                                        onChange={e => setEmail(e.target.value)} 
                                        placeholder="carlos@empresa.com.br"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Senha de Acesso {editingUser ? '' : '*'}
                                    </label>
                                    <PasswordInput 
                                        value={password} 
                                        onChange={e => setPassword(e.target.value)} 
                                        placeholder={editingUser ? 'Digite ou altere a senha' : 'Mínimo 6 caracteres'}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 pr-10 text-white text-sm focus:border-cyan-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Telefone / WhatsApp
                                    </label>
                                    <input 
                                        type="text" 
                                        value={phone} 
                                        onChange={e => setPhone(e.target.value)} 
                                        placeholder="(11) 99999-9999"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Grupo de Acesso
                                    </label>
                                    <select 
                                        value={isPortalUser ? 'User' : role} 
                                        onChange={e => !isPortalUser && setRole(e.target.value)}
                                        disabled={isPortalUser}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="User">User (Apenas os próprios chamados)</option>
                                        <option value="User Company">User Company (Chamados da Empresa + Módulos Selecionados)</option>
                                    </select>
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        {isPortalUser 
                                            ? '• Como Gestor (User Company), você só tem permissão para criar usuários do tipo User comum.' 
                                            : (role === 'User' 
                                                ? '• Acessa chamados abertos por ele mesmo e dados cadastrais básicos da empresa.' 
                                                : '• Acessa todos os chamados da empresa, dados cadastrais e os módulos marcados abaixo.')}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Status do Usuário
                                    </label>
                                    <select 
                                        value={active ? 'true' : 'false'} 
                                        onChange={e => setActive(e.target.value === 'true')}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none cursor-pointer"
                                    >
                                        <option value="true">Ativo (Login permitido)</option>
                                        <option value="false">Inativo (Bloqueado)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Módulos visíveis para User Company */}
                            {role === 'User Company' && (
                                <div className="bg-slate-900/60 p-4 rounded-2xl border border-cyan-500/20 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className="text-xs font-black uppercase tracking-wider text-cyan-400 block">
                                                Módulos Adicionais Permitidos (User Company)
                                            </span>
                                            <p className="text-[11px] text-slate-400">
                                                Marque os módulos da empresa que este gestor poderá visualizar na Área do Cliente.
                                            </p>
                                        </div>
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                                            Chamados: Sempre Ativo
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                                        <label className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                                            modules.contracts 
                                                ? 'bg-cyan-500/10 border-cyan-500/40 text-white' 
                                                : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:border-slate-700'
                                        }`}>
                                            <input 
                                                type="checkbox" 
                                                checked={modules.contracts} 
                                                onChange={e => setModules(prev => ({ ...prev, contracts: e.target.checked }))}
                                                className="mt-0.5 rounded text-cyan-500 focus:ring-0 focus:ring-offset-0 bg-slate-800 border-slate-700 h-4 w-4"
                                            />
                                            <div>
                                                <span className="text-xs font-bold block">Gestão do Contrato</span>
                                                <span className="text-[10px] text-slate-400 block leading-tight">Visualizar contratos e versões digitais</span>
                                            </div>
                                        </label>

                                        <label className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                                            modules.reports 
                                                ? 'bg-cyan-500/10 border-cyan-500/40 text-white' 
                                                : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:border-slate-700'
                                        }`}>
                                            <input 
                                                type="checkbox" 
                                                checked={modules.reports} 
                                                onChange={e => setModules(prev => ({ ...prev, reports: e.target.checked }))}
                                                className="mt-0.5 rounded text-cyan-500 focus:ring-0 focus:ring-offset-0 bg-slate-800 border-slate-700 h-4 w-4"
                                            />
                                            <div>
                                                <span className="text-xs font-bold block">Laudos Técnicos</span>
                                                <span className="text-[10px] text-slate-400 block leading-tight">Visualizar laudos de reparos e custos</span>
                                            </div>
                                        </label>

                                        <label className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                                            modules.assets 
                                                ? 'bg-cyan-500/10 border-cyan-500/40 text-white' 
                                                : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:border-slate-700'
                                        }`}>
                                            <input 
                                                type="checkbox" 
                                                checked={modules.assets} 
                                                onChange={e => setModules(prev => ({ ...prev, assets: e.target.checked }))}
                                                className="mt-0.5 rounded text-cyan-500 focus:ring-0 focus:ring-offset-0 bg-slate-800 border-slate-700 h-4 w-4"
                                            />
                                            <div>
                                                <span className="text-xs font-bold block">Monitoramento</span>
                                                <span className="text-[10px] text-slate-400 block leading-tight">Dispositivos e ativos monitorados</span>
                                            </div>
                                        </label>

                                        <div className="flex flex-col gap-2">
                                            <label className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                                                modules.inventory 
                                                    ? 'bg-cyan-500/10 border-cyan-500/40 text-white' 
                                                    : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:border-slate-700'
                                            }`}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={modules.inventory} 
                                                    onChange={e => setModules(prev => ({ ...prev, inventory: e.target.checked }))}
                                                    className="mt-0.5 rounded text-cyan-500 focus:ring-0 focus:ring-offset-0 bg-slate-800 border-slate-700 h-4 w-4"
                                                />
                                                <div>
                                                    <span className="text-xs font-bold block">Inventário</span>
                                                    <span className="text-[10px] text-slate-400 block leading-tight">Inventário de equipamentos da empresa</span>
                                                </div>
                                            </label>

                                            {/* Subitens granulares do inventário */}
                                            {modules.inventory && (
                                                <div className="ml-7 pl-3 border-l-2 border-slate-700/50 flex flex-col gap-1.5 mt-1">
                                                    {Object.keys(EQUIPMENT_CONFIG).sort().map(type => (
                                                        <label key={type} className="flex items-center gap-2 cursor-pointer group">
                                                            <input 
                                                                type="checkbox"
                                                                checked={modules.inventoryTypes?.includes(type)}
                                                                onChange={(e) => {
                                                                    setModules(prev => {
                                                                        const current = prev.inventoryTypes || [];
                                                                        if (e.target.checked) {
                                                                            return { ...prev, inventoryTypes: [...current, type] };
                                                                        } else {
                                                                            return { ...prev, inventoryTypes: current.filter(t => t !== type) };
                                                                        }
                                                                    });
                                                                }}
                                                                className="rounded text-cyan-500 focus:ring-0 focus:ring-offset-0 bg-slate-800 border-slate-700 h-3 w-3"
                                                            />
                                                            <span className="text-[10px] text-slate-400 group-hover:text-slate-300 transition-colors">
                                                                {type}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-3 border-t border-slate-700/80">
                                <button 
                                    type="button" 
                                    onClick={() => setIsFormOpen(false)}
                                    className="px-5 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-bold transition-all"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={saving}
                                    className="px-6 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-cyan-900/30 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full"></div>
                                            <span>Salvando...</span>
                                        </>
                                    ) : (
                                        editingUser ? 'Atualizar Usuário' : 'Criar Usuário'
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Tabela de Usuários */}
                    {loading ? (
                        <div className="py-12 flex justify-center items-center text-slate-400 text-sm animate-pulse">
                            Carregando usuários do portal...
                        </div>
                    ) : users.length === 0 ? (
                        <div className="py-12 text-center bg-slate-800/20 border border-dashed border-slate-700/60 rounded-2xl">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-slate-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <p className="text-slate-400 text-sm font-medium">Nenhum usuário cadastrado para esta empresa.</p>
                            <p className="text-slate-500 text-xs mt-1">Clique em "Adicionar Usuário" para permitir que colaboradores abram chamados.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-slate-700/60 bg-slate-800/30">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-800/80 border-b border-slate-700/80 text-[11px] font-black uppercase tracking-wider text-slate-400">
                                        <th className="py-2.5 px-4 whitespace-nowrap">Colaborador</th>
                                        <th className="py-2.5 px-4 whitespace-nowrap">E-mail</th>
                                        <th className="py-2.5 px-4 whitespace-nowrap">Telefone</th>
                                        <th className="py-2.5 px-4 whitespace-nowrap">Grupo de Acesso</th>
                                        <th className="py-2.5 px-4 whitespace-nowrap">Status</th>
                                        {canEdit && <th className="py-2.5 px-4 text-right whitespace-nowrap">Ações</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/40 text-xs">
                                    {filteredUsers.map(u => (
                                        <tr key={u.id} className="hover:bg-slate-700/30 transition-colors">
                                            <td className="py-2.5 px-4 font-bold text-white whitespace-nowrap">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center font-black text-cyan-400 text-xs shrink-0">
                                                        {(u.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <span>{u.name}</span>
                                                </div>
                                            </td>
                                            <td className="py-2.5 px-4 text-slate-300 font-mono whitespace-nowrap">{u.email}</td>
                                            <td className="py-2.5 px-4 text-slate-400 whitespace-nowrap">{u.phone || '—'}</td>
                                            <td className="py-2.5 px-4 whitespace-nowrap">
                                                {u.role === 'User Company' ? (
                                                    <div className="space-y-1">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                                                            User Company (Gestor)
                                                        </span>
                                                        <div className="flex flex-wrap gap-1">
                                                            {(!u.modules || u.modules.contracts !== false) && (
                                                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 border border-slate-700 font-medium">Contratos</span>
                                                            )}
                                                            {(!u.modules || u.modules.reports !== false) && (
                                                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 border border-slate-700 font-medium">Laudos</span>
                                                            )}
                                                            {(!u.modules || u.modules.assets !== false) && (
                                                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-800 text-slate-300 border border-slate-700 font-medium">Ativos</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                                                        User (Individual)
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-4 whitespace-nowrap">
                                                {u.active !== false ? (
                                                    <span className="text-[11px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                                        Ativo
                                                    </span>
                                                ) : (
                                                    <span className="text-[11px] font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20">
                                                        Inativo
                                                    </span>
                                                )}
                                            </td>
                                            {canEdit && (
                                                <td className="py-2.5 px-4 text-right whitespace-nowrap">
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <button 
                                                            onClick={() => handleSendEmail(u)}
                                                            disabled={sendingEmailId === u.id}
                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                sendingEmailId === u.id 
                                                                    ? 'text-cyan-400 bg-slate-700/60 opacity-75 cursor-wait' 
                                                                    : 'text-slate-400 hover:text-emerald-400 hover:bg-slate-700/60'
                                                            }`}
                                                            title="Enviar dados por e-mail automaticamente"
                                                        >
                                                            {sendingEmailId === u.id ? (
                                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            ) : (
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                        <button 
                                                            onClick={() => handleOpenEdit(u)}
                                                            className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-slate-700/60 rounded-lg transition-colors"
                                                            title="Editar usuário"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" />
                                                            </svg>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteUser(u)}
                                                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700/60 rounded-lg transition-colors"
                                                            title="Excluir usuário"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Rodapé */}
                <footer className="p-6 border-t border-slate-800 bg-slate-800/30 flex justify-end">
                    <button 
                        onClick={onClose}
                        className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs uppercase tracking-wider border border-slate-700 transition-all"
                    >
                        Fechar
                    </button>
                </footer>
            </div>

            <ConfirmationModal />

            {/* CSV Preview Modal */}
            {isCsvPreviewModalOpen && csvPreviewData && (
                <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm flex justify-center items-center z-[150] p-4 animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden max-h-[90vh]">
                        <header className="p-6 border-b border-slate-800 bg-slate-800/30">
                            <h3 className="text-xl font-black text-white">Pré-visualização da Importação</h3>
                            <p className="text-xs text-slate-400 mt-1">Verifique os dados antes de confirmar. Apenas linhas válidas serão importadas.</p>
                        </header>
                        
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-900/50 custom-scrollbar space-y-3">
                            {csvPreviewData.map((row, i) => (
                                <div key={i} className={`p-4 rounded-xl border flex items-center gap-4 ${row.isValid ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                                    <div className="shrink-0">
                                        {row.isValid ? (
                                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-lg">✓</div>
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-lg">✕</div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className="text-sm font-bold text-white truncate">{row.name || '(Sem Nome)'}</p>
                                            <span className="text-[10px] font-mono text-slate-500 ml-4 shrink-0 bg-slate-800 px-2 py-0.5 rounded border border-slate-700">{row.role || 'User'}</span>
                                        </div>
                                        <p className="text-xs text-slate-400 truncate">{row.email || '(Sem E-mail)'}</p>
                                        
                                        {!row.isValid && (
                                            <div className="mt-2 text-[11px] text-red-400 font-medium">
                                                Erro: {row.errors.join(' | ')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <footer className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-800/30">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsCsvPreviewModalOpen(false);
                                    setCsvPreviewData(null);
                                }}
                                className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800 hover:text-white transition-all border border-transparent hover:border-slate-700"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmImportCsv}
                                disabled={saving || !csvPreviewData.some(d => d.isValid)}
                                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-emerald-900/20 flex items-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Importando...
                                    </>
                                ) : (
                                    `Confirmar Importação (${csvPreviewData.filter(d => d.isValid).length})`
                                )}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
